import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Container, ReportScoringBandsLegend, SimpleButton, Loading, ErrorMessage } from '@/src/ui';
import { getPdfFileName } from '@/src/utils';
import ItemAnalysisReportFilters from './components/ItemAnalysisFilters';
import ItemAnalysisReportDataTable from './components/ItemAnalysisTable';
import ItemAnalysisLegend from './components/ItemAnalysisLegend';
import type { ItemAnalysisDTO } from './models/itemAnalysisDTOs';
import type { ItemAnalysisReportFilterState } from './models/itemAnalysisFilterDTOs';
import QuestionDetailsModal from './components/QuestionDetailsModal';
import { useAuth } from '@/src/app/context/useAuth';

import './ItemAnalysisPage.css';

const ItemAnalysisPage: React.FC = () => {
    document.title = 'Item Analysis Report';

    const [data, setData] = useState<ItemAnalysisDTO | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [showMatrixLegend, setShowMatrixLegend] = useState(false);
    const [showReportingBands, setShowReportingBands] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const pendingExamNameRef = useRef('');
    const [displayedExamName, setDisplayedExamName] = useState(() => {
        try {
            const saved = localStorage.getItem(`universal-filters-${userId}`);
            if (saved) return JSON.parse(saved)?.examName || '';
        } catch {
            // intentionally empty
        }
        return '';
    });

    const [filterState, setFilterState] = useState<ItemAnalysisReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
                examName: displayedExamName,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, displayedExamName, userId, UNIVERSAL_FILTER_KEY]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);

    const handleQuestionClick = (questionId: number) => {
        setSelectedQuestionId(Number(questionId));
        setModalOpen(true);
    };

    const refreshTableData = useCallback(
        async (filters: ItemAnalysisReportFilterState = filterState) => {
            setError('');
            setLoaded(false);

            if (!filters.districtId || !filters.schoolId || !filters.classId || !filters.examId) {
                setError('Please select Grade, District, School, Class, and Assessment.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/itemAnalysis?districtId=${filters.districtId}`;
            requestUrl += `&schoolId=${filters.schoolId}`;
            requestUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
            requestUrl += `&classId=${filters.classId}`;
            requestUrl += `&examId=${filters.examId}`;
            requestUrl += `&examTypeMasterId=${filters.examTypeMasterId}`;

            try {
                const res = await apiRequest<ItemAnalysisDTO>('GET', requestUrl);
                setData(res.data ?? null);
                if (pendingExamNameRef.current) setDisplayedExamName(pendingExamNameRef.current);
            } catch {
                setError('Failed to load Item Analysis Report');
                setData(null);
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        setData(null);
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId && fs.classId && fs.examId) {
            refreshTableData();
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dataLoaded = data !== null && data.questions.length !== 0;

    return (
        <Container>
            <h1 className="page-header">Item Analysis Report</h1>
            <br />

            <Box>
                <ItemAnalysisReportFilters
                    filterState={filterState}
                    setFilterState={setFilterState}
                    refreshTableData={refreshTableData}
                    onExamNameChange={(name) => {
                        pendingExamNameRef.current = name;
                    }}
                />
            </Box>

            <Loading visible={!loaded} message="Loading Item Analysis Report..." />
            <ErrorMessage error={error} style={{ textAlign: 'center', marginBottom: '8px' }} />
            {loaded && data && displayedExamName && (
                <h2
                    style={{
                        fontSize: '24px',
                        fontWeight: 600,
                        marginBottom: '8px',
                    }}
                >
                    {displayedExamName}
                </h2>
            )}

            <Box sx={{ pb: 2 }}>
                {/* Button row */}
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    {dataLoaded && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                            }}
                        >
                            <SimpleButton
                                variant="outlined"
                                onClick={() => setShowMatrixLegend(!showMatrixLegend)}
                                aria-expanded={showMatrixLegend}
                                aria-label="Toggle legend dropdown"
                            >
                                {showMatrixLegend ? 'Hide Legend' : 'Show Legend'}
                            </SimpleButton>
                            <SimpleButton
                                variant="outlined"
                                onClick={() => setShowReportingBands(!showReportingBands)}
                                aria-expanded={showReportingBands}
                                aria-label="Toggle reporting bands dropdown"
                            >
                                {showReportingBands ? 'Hide Scoring Bands' : 'Show Scoring Bands'}
                            </SimpleButton>
                        </Box>
                    )}

                    {filterState.examId !== null && filterState.examId !== 0 && (
                        <Button
                            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                            variant="outlined"
                            href={`/reports/questionList/${filterState.examId}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Assessment
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <ItemAnalysisLegend dataLoaded={dataLoaded} showLegend={showMatrixLegend} />
                    <ReportScoringBandsLegend
                        dataLoaded={dataLoaded}
                        showLegend={showReportingBands}
                    />
                </Box>
            </Box>

            <div className="table-container" style={{ overflow: 'auto' }}>
                {loaded && data && (
                    <ItemAnalysisReportDataTable
                        data={data}
                        onQuestionClick={handleQuestionClick}
                        pdfFileName={getPdfFileName(displayedExamName, 'Item Analysis')}
                    />
                )}
            </div>

            {selectedQuestionId && (
                <QuestionDetailsModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    filterState={filterState}
                    questionId={selectedQuestionId}
                />
            )}
        </Container>
    );
};

export default ItemAnalysisPage;
